import React, { useState, useEffect, useRef } from 'react';
import styles from './CSS/SearchPage.module.css';
import { Search, X, BookOpen, Music, Film, Loader, ArrowLeft, ChevronLeft } from 'lucide-react';
import { useSearch, useSearchSuggestions, useTrendingSearches } from '../../hooks/useSearchHooks';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';

const SearchPage = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const [searchOptions, setSearchOptions] = useState({
    page: 1,
    limit: 20,
    contentType: '',
    language: '',
    categoryId: ''
  });
  const [showSuggestions, setShowSuggestions] = useState(false);
  const searchInputRef = useRef(null);
  const suggestionsRef = useRef(null); // Add ref for suggestions container
  const navigate = useNavigate();

  // Custom hooks for search functionality
  const { 
    searchResults, 
    groupedResults, 
    meta, 
    isLoading 
  } = useSearch(searchQuery, searchOptions);

  const { 
    suggestions, 
    isLoadingSuggestions 
  } = useSearchSuggestions(searchQuery);

  const { 
    trendingSearches, 
    isLoadingTrending 
  } = useTrendingSearches();

  // Handle click outside to close suggestions
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Check if click is outside both search input and suggestions container
      if (
        searchInputRef.current && 
        !searchInputRef.current.contains(event.target) &&
        suggestionsRef.current &&
        !suggestionsRef.current.contains(event.target)
      ) {
        setShowSuggestions(false);
        setIsSearchFocused(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Clear search function
  const clearSearch = () => {
    setSearchQuery('');
    setSearchOptions({
      ...searchOptions,
      page: 1
    });
  };

  // Handle content type filter change
  const handleFilterChange = (type) => {
    setSearchOptions({
      ...searchOptions,
      contentType: type === searchOptions.contentType ? '' : type,
      page: 1
    });
  };

  // Handle back navigation
  const handleBackClick = () => {
    navigate('/'); // or navigate(-1) to go back to previous page
  };

  // Handle navigation to content page
  const handleContentClick = (item) => {
    let path = '';
    
    // For audio content
    if (item.content_type === 'audio') {
      if (item.have_series && item.series_data) {
        path = `/audio-play/${item.slug}`;
      } else {
        path = `/audio-play/${item.slug}`;
      }
    } 
    // For ebook content
    else if (item.content_type === 'ebook') {
      if (item.have_series && item.series_data) {
        path = `/book-details/${item.slug}`;
      } else {
        path = `/book-detail/${item.slug}`;
      }
    } 
    // For video content
    else if (item.content_type === 'video') {
      if (item.have_series && item.series_data) {
        path = `/video-series/${item.series_data.slug}`;
      } else {
        path = `/video/${item.slug}`;
      }
    } 
    // Default fallback
    else {
      path = '/';
    }
    
    navigate(path);
  };

  // Handle suggestion click
  const handleSuggestionClick = (suggestion) => {
    setSearchQuery(suggestion.title);
    setShowSuggestions(false);
    setIsSearchFocused(false); // Also set focus to false
    setSearchOptions({
      ...searchOptions,
      contentType: suggestion.content_type,
      page: 1
    });
  };

  // Handle trending search click
  const handleTrendingClick = (trending) => {
    setSearchQuery(trending.title);
    setShowSuggestions(false);
    setIsSearchFocused(false); // Also set focus to false
    setSearchOptions({
      ...searchOptions,
      contentType: trending.content_type,
      page: 1
    });
  };

  // Load more results
  const loadMoreResults = () => {
    if (meta.page < meta.total_pages) {
      setSearchOptions({
        ...searchOptions,
        page: searchOptions.page + 1
      });
    }
  };

  // Get content type icon
  const getContentTypeIcon = (type) => {
    switch(type) {
      case 'audio':
        return <Music size={16} className={styles.contentTypeIcon} />;
      case 'ebook':
        return <BookOpen size={16} className={styles.contentTypeIcon} />;
      case 'video':
        return <Film size={16} className={styles.contentTypeIcon} />;
      default:
        return null;
    }
  };

  return (
    <>
    <Helmet>
      <title>Search - SunoKahaniyan</title>
      <meta name="description" content="Search for audios, ebooks, and videos on SunoKahaniyan. Explore trending searches and suggestions." />
    </Helmet>
    <div className={styles.searchPageContainer}>
      {/* Mobile Header with Search Bar */}
      <div className={styles.mobileHeader}>
       
        <div className={styles.mobileSearchContainer}>
        <button 
          className={styles.backButton}
          onClick={handleBackClick}
        >
        <ChevronLeft size={20} strokeWidth={2.5} color='white' />
        </button>
          <div 
            ref={searchInputRef}
            className={`${styles.mobileSearchInput} ${isSearchFocused ? styles.focused : ''}`}
          >
            <div className={styles.searchInputWrapper}>
              <Search 
                className={styles.searchIcon} 
                size={20} 
                strokeWidth={2.5}
                color='black'
              />
              <input 
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onFocus={() => {
                  setIsSearchFocused(true);
                  setShowSuggestions(true);
                }}
                placeholder="Search for audios, ebooks, series..."
                className={styles.searchInput}
              />
              {isLoadingSuggestions && (
                <Loader size={20} className={`${styles.loadingIcon} ${styles.spinning}`} />
              )}
              {searchQuery && (
                <button 
                  type="button"
                  onClick={clearSearch}
                  className={styles.clearButton}
                >
                  <X size={20} strokeWidth={2.5} color='black'/>
                </button>
              )}
            </div>

            {/* Mobile Search Suggestions */}
            {showSuggestions && searchQuery && suggestions.length > 0 && (
              <div 
                ref={suggestionsRef}
                className={styles.mobileSuggestionsContainer}
              >
                <div className={styles.suggestionsList}>
                  <h4 className={styles.suggestionsHeader}>Suggestions</h4>
                  <ul>
                    {suggestions.map((suggestion, index) => (
                      <li 
                        key={`suggestion-${index}`}
                        className={styles.suggestionItem}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleSuggestionClick(suggestion);
                        }}
                      >
                        {getContentTypeIcon(suggestion.content_type)}
                        <span>{suggestion.title}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            )}
          </div>
        </div>
         
      </div>

      <div className={styles.searchWrapper}>
        <div 
          ref={searchInputRef}
          className={`${styles.searchContainer} ${isSearchFocused ? styles.focused : ''}`}
        >
          <div className={styles.searchInputWrapper}>
            <Search 
              className={styles.searchIcon} 
              size={20} 
              strokeWidth={2.5}
            />
            <input 
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onFocus={() => {
                setIsSearchFocused(true);
                setShowSuggestions(true);
              }}
              placeholder="Search for audios, ebooks, series..."
              className={styles.searchInput}
            />
            {isLoadingSuggestions && (
              <Loader size={20} className={`${styles.loadingIcon} ${styles.spinning}`} />
            )}
            {searchQuery && (
              <button 
                type="button"
                onClick={clearSearch}
                className={styles.clearButton}
              >
                <X size={20} strokeWidth={2.5} />
              </button>
            )}
          </div>

          {/* Search Suggestions */}
          {showSuggestions && (searchQuery || trendingSearches.length > 0) && (
            <div 
              ref={suggestionsRef}
              className={styles.suggestionsContainer}
            >
              {searchQuery && suggestions.length > 0 && (
                <div className={styles.suggestionsList}>
                  <h4 className={styles.suggestionsHeader}>Suggestions</h4>
                  <ul>
                    {suggestions.map((suggestion, index) => (
                      <li 
                        key={`suggestion-${index}`}
                        className={styles.suggestionItem}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleSuggestionClick(suggestion);
                        }}
                      >
                        {getContentTypeIcon(suggestion.content_type)}
                        <span>{suggestion.title}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {!searchQuery && trendingSearches.length > 0 && (
                <div className={styles.trendingContainer}>
                  <h4 className={styles.trendingHeader}>Trending</h4>
                  <ul className={styles.trendingList}>
                    {trendingSearches.map((trending, index) => (
                      <li 
                        key={`trending-${index}`}
                        className={styles.trendingItem}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleTrendingClick(trending);
                        }}
                      >
                        {getContentTypeIcon(trending.content_type)}
                        <span>{trending.title}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Content Type Filters */}
        {searchQuery && (
          <div className={styles.filtersContainer}>
            <button 
              className={`${styles.filterButton} ${searchOptions.contentType === '' ? styles.active : ''}`}
              onClick={() => handleFilterChange('')}
            >
              All
            </button>
            <button 
              className={`${styles.filterButton} ${searchOptions.contentType === 'audio' ? styles.active : ''}`}
              onClick={() => handleFilterChange('audio')}
            >
              <Music size={16} />
              Audios
            </button>
            <button 
              className={`${styles.filterButton} ${searchOptions.contentType === 'ebook' ? styles.active : ''}`}
              onClick={() => handleFilterChange('ebook')}
            >
              <BookOpen size={16} />
              Ebooks
            </button>
            <button 
              className={`${styles.filterButton} ${searchOptions.contentType === 'series' ? styles.active : ''}`}
              onClick={() => handleFilterChange('video')}
            >
              <Film size={16} />
              Videos
            </button>
          </div>
        )}

        {/* Search Results */}
        {searchQuery && (
          <div className={styles.resultsContainer}>
            {isLoading && searchOptions.page === 1 ? (
              <div className={styles.loadingContainer}>
                <Loader size={40} className={`${styles.loadingIcon} ${styles.spinning}`} />
                <p>Searching for "{searchQuery}"...</p>
              </div>
            ) : (
              <>
                {/* Results Header */}
                {searchResults.length > 0 && (
                  <div className={styles.resultsHeader}>
                    <h2 className={styles.resultsTitle}>
                      {meta.total} results for "{searchQuery}"
                    </h2>
                  </div>
                )}

                {/* Results Grid */}
                {searchResults.length > 0 ? (
                  <div className={styles.resultsGrid}>
                    {searchResults.map((item) => (
                      <div 
                        key={`${item.content_type}-${item.id}`} 
                        className={styles.resultCard}
                        onClick={() => handleContentClick(item)}
                      >
                        <div className={styles.resultImageContainer}>
                          <img 
                            src={item.mobile_image_url || '/default-thumbnail.png'} 
                            alt={item.title}
                            className={styles.resultImage}
                          />
                          <span className={styles.contentTypeBadge}>
                            {getContentTypeIcon(item.content_type)}
                            {item.content_type}
                          </span>
                        </div>
                        
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className={styles.noResults}>
                    <p>No results found for "{searchQuery}"</p>
                    <p>Try different keywords or browse our trending content</p>
                  </div>
                )}

                {/* Load More Button */}
                {meta.page < meta.total_pages && (
                  <button 
                    className={styles.loadMoreButton}
                    onClick={loadMoreResults}
                    disabled={isLoading && searchOptions.page > 1}
                  >
                    {isLoading && searchOptions.page > 1 ? (
                      <>
                        <Loader size={16} className={`${styles.loadingIcon} ${styles.spinning}`} />
                        Loading more results...
                      </>
                    ) : (
                      <>Load More Results</>
                    )}
                  </button>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </div>
    </>
  );
};

export default SearchPage;